/* TerritoryMap — rural territory map for the town-by-town import.
 *
 * Two modes:
 *   • VIEW (default): draws the saved boundary (rectangle box, or the actual
 *     polygon outline when the territory was free-drawn), dims everything
 *     outside it, and drops a pin per town (brand red = included for import,
 *     slate = excluded). Tapping a pin toggles that town's checkbox.
 *   • DRAW (`drawMode`): renders a single EDITABLE + draggable polygon seeded
 *     from `drawPolygon`. The user drags the vertices (and Google's midpoint
 *     handles) to reshape it; every edit is reported back via `onPathChange`.
 *
 * Shares the app-wide `gmaps-script` loader WITH the `places` library so it
 * matches the other map components (OpenHouseMap / BrokerageMap) — a mismatch
 * throws "Loader must not be called again with different options".
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Rectangle,
  Polygon,
} from "@react-google-maps/api";
import { AlertTriangle } from "lucide-react";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
// Constant array ref so useJsApiLoader doesn't reload on every render, and so
// the shared loader options match OpenHouseMap / BrokerageMap exactly.
const LIBRARIES = ["places"];
const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
};

const BRAND = "#C8102E";
const SLATE = "#94A3B8";
const WORLD_RING = [
  { lat: 85, lng: -179.999 },
  { lat: 85, lng: 179.999 },
  { lat: -85, lng: 179.999 },
  { lat: -85, lng: -179.999 },
];

function dotSymbol(color, scale) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 1,
    strokeWeight: 1.5,
    strokeColor: "#ffffff",
  };
}

function cityPin(color) {
  return {
    path: "M 0,0 C -2,-20 -10,-22 -10,-30 a 10,10 0 1,1 20,0 c 0,8 -8,10 -10,30 z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.15,
    anchor: new window.google.maps.Point(0, 0),
  };
}

function boxRing(box) {
  return [
    { lat: box.north, lng: box.west },
    { lat: box.south, lng: box.west },
    { lat: box.south, lng: box.east },
    { lat: box.north, lng: box.east },
  ];
}

export default function TerritoryMap({
  box,
  center,
  zoom = 8,
  towns,
  checked,
  onToggleTown,
  // --- draw mode ---
  drawMode = false,
  drawPolygon = null, // [{lat,lng}, ...] — stable seed; edits reported via onPathChange
  onPathChange,
  // --- view mode extras ---
  outlinePolygon = null, // [{lat,lng}, ...] — real drawn shape (preferred over box)
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });
  const [activeTown, setActiveTown] = useState(null);
  const mapRef = useRef(null);
  const polyRef = useRef(null);
  const listenersRef = useRef([]);

  const points = useMemo(
    () => (towns || []).filter((t) => typeof t.lat === "number" && typeof t.lng === "number"),
    [towns],
  );

  // bbox used to fit the viewport on first load
  const fitBox = useMemo(() => {
    if (drawMode && Array.isArray(drawPolygon) && drawPolygon.length) {
      const lats = drawPolygon.map((p) => p.lat);
      const lngs = drawPolygon.map((p) => p.lng);
      return { north: Math.max(...lats), south: Math.min(...lats), east: Math.max(...lngs), west: Math.min(...lngs) };
    }
    if (outlinePolygon && outlinePolygon.length >= 3) {
      const lats = outlinePolygon.map((p) => p.lat);
      const lngs = outlinePolygon.map((p) => p.lng);
      return { north: Math.max(...lats), south: Math.min(...lats), east: Math.max(...lngs), west: Math.min(...lngs) };
    }
    return box || null;
  }, [drawMode, drawPolygon, outlinePolygon, box]);

  // World ring + hole → dims everything OUTSIDE the territory (view mode only).
  const maskPaths = useMemo(() => {
    if (drawMode) return null;
    let hole = null;
    if (outlinePolygon && outlinePolygon.length >= 3) hole = outlinePolygon;
    else if (box) hole = boxRing(box);
    if (!hole) return null;
    return [WORLD_RING, hole];
  }, [drawMode, outlinePolygon, box]);

  const effectiveCenter = useMemo(() => {
    if (center) return center;
    if (fitBox) return { lat: (fitBox.north + fitBox.south) / 2, lng: (fitBox.east + fitBox.west) / 2 };
    return { lat: 52.0, lng: -106.0 };
  }, [center, fitBox]);

  const onLoadMap = useCallback(
    (map) => {
      mapRef.current = map;
      try {
        if (fitBox) {
          const b = new window.google.maps.LatLngBounds(
            { lat: fitBox.south, lng: fitBox.west },
            { lat: fitBox.north, lng: fitBox.east },
          );
          map.fitBounds(b, 24);
        }
      } catch {
        /* ignore */
      }
    },
    [fitBox],
  );

  // --- editable polygon wiring (draw mode) ---
  const readPath = useCallback(() => {
    const poly = polyRef.current;
    if (!poly || !onPathChange) return;
    const path = poly.getPath();
    const arr = [];
    for (let i = 0; i < path.getLength(); i++) {
      const ll = path.getAt(i);
      arr.push({ lat: ll.lat(), lng: ll.lng() });
    }
    onPathChange(arr);
  }, [onPathChange]);

  const onPolyLoad = useCallback(
    (poly) => {
      polyRef.current = poly;
      const path = poly.getPath();
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [
        path.addListener("set_at", readPath),
        path.addListener("insert_at", readPath),
        path.addListener("remove_at", readPath),
        poly.addListener("dragend", readPath),
      ];
      readPath();
      try {
        if (mapRef.current) {
          const b = new window.google.maps.LatLngBounds();
          for (let i = 0; i < path.getLength(); i++) b.extend(path.getAt(i));
          mapRef.current.fitBounds(b, 40);
        }
      } catch {
        /* ignore */
      }
    },
    [readPath],
  );

  const onPolyUnmount = useCallback(() => {
    listenersRef.current.forEach((l) => l.remove());
    listenersRef.current = [];
    polyRef.current = null;
  }, []);

  if (loadError) {
    return (
      <section className="rounded-lg border border-red-200 bg-red-50 p-6" data-testid="territory-map-load-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-red-900">Google Maps failed to load</div>
            <p className="text-xs text-red-700 mt-1">{String(loadError.message || loadError)}</p>
          </div>
        </div>
      </section>
    );
  }
  if (!apiKey) {
    return (
      <section className="rounded-lg border border-amber-200 bg-amber-50 p-6" data-testid="territory-map-no-key">
        <div className="text-sm font-semibold text-amber-900">Map view unavailable</div>
        <p className="text-xs text-amber-700 mt-1">
          Set REACT_APP_GOOGLE_MAPS_API_KEY in /app/frontend/.env to enable the map.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-slate-200 overflow-hidden bg-white"
      data-testid={drawMode ? "territory-draw-map" : "territory-map-view"}
    >
      <div
        className="relative w-full"
        style={{ height: "62vh", minHeight: "460px" }}
        data-testid="territory-map-container"
      >
        {!isLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={effectiveCenter}
            zoom={zoom}
            onLoad={onLoadMap}
            options={MAP_OPTIONS}
          >
            {maskPaths ? (
              <Polygon
                paths={maskPaths}
                options={{ fillColor: "#0F172A", fillOpacity: 0.28, strokeWeight: 0, clickable: false, zIndex: 1 }}
              />
            ) : null}

            {/* View-mode boundary: real polygon outline when available, else box */}
            {!drawMode && outlinePolygon && outlinePolygon.length >= 3 ? (
              <Polygon
                paths={outlinePolygon}
                options={{ fillOpacity: 0, strokeColor: BRAND, strokeWeight: 2.5, clickable: false, zIndex: 2 }}
              />
            ) : !drawMode && box ? (
              <Rectangle
                bounds={box}
                options={{ fillOpacity: 0, strokeColor: BRAND, strokeWeight: 2.5, clickable: false, zIndex: 2 }}
              />
            ) : null}

            {/* Draw-mode editable polygon */}
            {drawMode && Array.isArray(drawPolygon) && drawPolygon.length >= 3 ? (
              <Polygon
                path={drawPolygon}
                editable
                draggable
                onLoad={onPolyLoad}
                onUnmount={onPolyUnmount}
                options={{
                  editable: true,
                  draggable: true,
                  clickable: true,
                  fillColor: BRAND,
                  fillOpacity: 0.1,
                  strokeColor: BRAND,
                  strokeWeight: 2.5,
                  zIndex: 3,
                }}
              />
            ) : null}

            {points.map((t) => {
              const on = drawMode ? true : checked?.has(t.name);
              const color = on ? BRAND : SLATE;
              const icon = t.is_city ? cityPin(color) : dotSymbol(color, t.type === "town" ? 6 : 4.5);
              return (
                <Marker
                  key={t.name}
                  position={{ lat: t.lat, lng: t.lng }}
                  icon={icon}
                  clickable={!drawMode}
                  title={`${t.name}${!drawMode && !on ? " (excluded)" : ""}`}
                  onClick={drawMode ? undefined : () => setActiveTown(t.name)}
                  zIndex={t.is_city ? 20 : 10}
                  data-testid={`territory-pin-${t.name}`}
                />
              );
            })}

            {!drawMode && activeTown
              ? (() => {
                  const t = points.find((p) => p.name === activeTown);
                  if (!t) return null;
                  const on = checked?.has(t.name);
                  return (
                    <InfoWindow
                      position={{ lat: t.lat, lng: t.lng }}
                      onCloseClick={() => setActiveTown(null)}
                      options={{ pixelOffset: new window.google.maps.Size(0, t.is_city ? -30 : -6) }}
                    >
                      <div className="text-xs leading-snug" style={{ minWidth: 150 }}>
                        <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                        <div className="text-slate-500 capitalize mt-0.5">{t.type}</div>
                        <button
                          type="button"
                          onClick={() => {
                            onToggleTown?.(t.name);
                          }}
                          className={
                            "mt-2 w-full rounded-md px-2 py-1 text-[11px] font-medium text-white " +
                            (on ? "bg-slate-500 hover:bg-slate-600" : "bg-[#C8102E] hover:opacity-90")
                          }
                          data-testid={`territory-pin-toggle-${t.name}`}
                        >
                          {on ? "Exclude from import" : "Include in import"}
                        </button>
                      </div>
                    </InfoWindow>
                  );
                })()
              : null}
          </GoogleMap>
        )}
      </div>
      <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center gap-4 text-[11px] text-slate-500">
        {drawMode ? (
          <span className="text-slate-500" data-testid="territory-draw-hint">
            Drag the dots to reshape your boundary · drag the lighter dots to add new corners
          </span>
        ) : (
          <>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: BRAND }} /> Included
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: SLATE }} /> Excluded
            </span>
            <span className="hidden sm:inline text-slate-400">Tap a pin to toggle a town</span>
          </>
        )}
      </div>
    </section>
  );
}
